import React, { useState, useEffect } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight, Pencil, Home } from 'lucide-react';

const timeSlots = [];
for (let hour = 8; hour < 19; hour++) {
  timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
}

function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('workweek'); // 1day, 3day, workweek, 6day, 7day
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formData, setFormData] = useState({
    patientId: '',
    practitionerId: '',
    type: 'initial_assessment',
    appointmentNotes: ''
  });
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [touchDragAppointment, setTouchDragAppointment] = useState(null);
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
  const [touchTargetSlot, setTouchTargetSlot] = useState(null);
  const [fetchError, setFetchError] = useState('Init...');
  const [fetchCount, setFetchCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editForm, setEditForm] = useState({ appointmentDate: '', startTime: '', endTime: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      const weekStart = startOfWeek(selectedDay, { weekStartsOn: 1 });
      if (weekStart.getTime() !== currentWeekStart.getTime()) {
        setCurrentWeekStart(weekStart);
      }
    }
  }, [selectedDay, isMobile]);

  const practitionerColors = [
    '#2563eb', // blue
    '#7c3aed', // purple
    '#059669', // green
    '#dc2626', // red
    '#ea580c', // orange
    '#0891b2', // cyan
    '#c026d3', // fuchsia
    '#4f46e5', // indigo
  ];

  const getPractitionerColor = (practitionerId) => {
    const index = practitioners.findIndex(p => p.id === practitionerId);
    return practitionerColors[index % practitionerColors.length] || '#6b7280';
  };

  const getSlotSpan = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return Math.max(1, Math.ceil((endMinutes - startMinutes) / 30));
  };

  const timeToMinutes = (time) => {
    const [hour, min] = time.split(':').map(Number);
    return hour * 60 + min;
  };

  const getOverlappingGroups = (dayAppointments) => {
    if (!dayAppointments.length) return [];
    
    const sorted = [...dayAppointments].sort((a, b) => 
      timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );
    
    const positioned = [];
    
    sorted.forEach(apt => {
      const aptStart = timeToMinutes(apt.startTime);
      const aptEnd = timeToMinutes(apt.endTime);
      
      const overlapping = positioned.filter(p => {
        const pStart = timeToMinutes(p.appointment.startTime);
        const pEnd = timeToMinutes(p.appointment.endTime);
        return aptStart < pEnd && aptEnd > pStart;
      });
      
      let column = 0;
      const usedColumns = overlapping.map(o => o.column);
      while (usedColumns.includes(column)) column++;
      
      const maxColumns = Math.max(column + 1, ...overlapping.map(o => o.totalColumns));
      
      overlapping.forEach(o => o.totalColumns = maxColumns);
      
      positioned.push({
        appointment: apt,
        column,
        totalColumns: maxColumns
      });
    });
    
    positioned.forEach(p => {
      const aptStart = timeToMinutes(p.appointment.startTime);
      const aptEnd = timeToMinutes(p.appointment.endTime);
      
      const overlapping = positioned.filter(other => {
        if (other.appointment.id === p.appointment.id) return false;
        const oStart = timeToMinutes(other.appointment.startTime);
        const oEnd = timeToMinutes(other.appointment.endTime);
        return aptStart < oEnd && aptEnd > oStart;
      });
      
      const maxCols = Math.max(p.totalColumns, ...overlapping.map(o => o.totalColumns));
      p.totalColumns = maxCols;
      overlapping.forEach(o => o.totalColumns = maxCols);
    });
    
    return positioned;
  };

  const getViewDays = () => {
    switch (viewMode) {
      case '1day':
        return [selectedDay];
      case '3day':
        return Array.from({ length: 3 }, (_, i) => addDays(selectedDay, i - 1));
      case 'workweek':
        return Array.from({ length: 5 }, (_, i) => addDays(currentWeekStart, i));
      case '6day':
        return Array.from({ length: 6 }, (_, i) => addDays(currentWeekStart, i));
      case '7day':
      default:
        return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
    }
  };

  const weekDays = getViewDays();

  const fetchData = async (weekStart) => {
    const targetWeekStart = weekStart || currentWeekStart;
    setLoading(true);
    try {
      const startDate = format(targetWeekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(targetWeekStart, 6), 'yyyy-MM-dd');
      
      const cacheBuster = Date.now();
      const [appointmentsRes, patientsRes, usersRes] = await Promise.all([
        api.get(`/appointments?startDate=${startDate}&endDate=${endDate}&_t=${cacheBuster}`),
        api.get(`/patients?_t=${cacheBuster}`),
        api.get(`/auth/me?_t=${cacheBuster}`)
      ]);
      
      // On mobile, filter to show only the logged-in practitioner's appointments
      const allAppointments = appointmentsRes.appointments || [];
      const filteredAppointments = isMobile && user?.id 
        ? allAppointments.filter(apt => apt.practitionerId === user.id)
        : allAppointments;
      setAppointments(filteredAppointments);
      setPatients(patientsRes.patients || []);
      
      const allPractitioners = [];
      if (usersRes.user) {
        allPractitioners.push(usersRes.user);
      }
      if (usersRes.user?.clinic?.users) {
        usersRes.user.clinic.users.forEach(u => {
          if (!allPractitioners.find(p => p.id === u.id)) {
            allPractitioners.push(u);
          }
        });
      }
      const fromAppointments = (appointmentsRes.appointments || [])
        .map(a => a.practitioner)
        .filter(p => p && !allPractitioners.find(x => x.id === p.id));
      setPractitioners([...allPractitioners, ...fromAppointments]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load calendar data');
    }
    setLoading(false);
  };

  const loadCalendarData = async () => {
    setLoading(true);
    setFetchCount(c => c + 1);
    setFetchError('Fetching...');
    try {
      const startDate = format(currentWeekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      
      const cacheBuster = Date.now();
      const url = `/appointments?startDate=${startDate}&endDate=${endDate}&_t=${cacheBuster}`;
      
      setFetchError(`Calling: ${url.substring(0, 40)}...`);
      
      // Use fetch directly to bypass any axios issues
      const token = localStorage.getItem('token');
      const baseUrl = '/api';
      
      const appointmentsRes = await fetch(`${baseUrl}${url}&r=${Math.random()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      const rawText = await appointmentsRes.text();
      if (!appointmentsRes.ok) {
        setFetchError(`Appts ${appointmentsRes.status}: ${rawText.substring(0, 30)}`);
        setLoading(false);
        return;
      }
      let appointmentsData;
      try {
        appointmentsData = JSON.parse(rawText);
      } catch (e) {
        setFetchError(`Parse: ${rawText.substring(0, 50)}`);
        setLoading(false);
        return;
      }
      
      const patientsRes = await fetch(`${baseUrl}/patients?_t=${cacheBuster}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!patientsRes.ok) {
        setFetchError(`Pts ${patientsRes.status}`);
        setLoading(false);
        return;
      }
      const patientsData = await patientsRes.json();
      
      const usersRes = await fetch(`${baseUrl}/auth/me?_t=${cacheBuster}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!usersRes.ok) {
        setFetchError(`Me ${usersRes.status}`);
        setLoading(false);
        return;
      }
      const usersData = await usersRes.json();
      
      // On mobile, filter to show only the logged-in practitioner's appointments
      const allAppointments = appointmentsData.appointments || [];
      const filteredAppointments = isMobile && usersData?.user?.id 
        ? allAppointments.filter(apt => apt.practitionerId === usersData.user.id)
        : allAppointments;
      setAppointments(filteredAppointments);
      setPatients(patientsData.patients || []);
      
      const allPractitioners = [];
      if (usersData.user) {
        allPractitioners.push(usersData.user);
      }
      if (usersData.user?.clinic?.users) {
        usersData.user.clinic.users.forEach(u => {
          if (!allPractitioners.find(p => p.id === u.id)) {
            allPractitioners.push(u);
          }
        });
      }
      const fromAppointments = (allAppointments)
        .map(a => a.practitioner)
        .filter(p => p && !allPractitioners.find(x => x.id === p.id));
      setPractitioners([...allPractitioners, ...fromAppointments]);
      const cid = usersData?.user?.clinicId || 'none';
      setFetchError(`${startDate}-${endDate} C:${cid.substring(0,8)} A${filteredAppointments.length}`);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setFetchError('ERR: ' + (error?.message || String(error)));
      toast.error('Failed to load calendar data');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCalendarData();
  }, [currentWeekStart]);

  const getAppointmentsForSlot = (day, time) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const slotTime = time + ':00';
    return appointments.filter(apt => {
      const aptStart = apt.startTime.substring(0, 8);
      const aptEnd = apt.endTime.substring(0, 8);
      const matchesPractitioner = selectedPractitioner === 'all' || apt.practitionerId === selectedPractitioner;
      return apt.appointmentDate === dateStr && 
        aptStart <= slotTime && 
        aptEnd > slotTime &&
        matchesPractitioner;
    });
  };

  const getAppointmentForSlot = (day, time) => {
    return getAppointmentsForSlot(day, time)[0] || null;
  };

  const handleSlotClick = (day, time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const endHour = minutes === 30 ? hours + 1 : hours;
    const endMinutes = minutes === 30 ? '00' : '30';
    
    setSelectedSlot({
      date: format(day, 'yyyy-MM-dd'),
      startTime: time,
      endTime: `${endHour.toString().padStart(2, '0')}:${endMinutes}`
    });
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      practitionerId: selectedPractitioner !== 'all' ? selectedPractitioner : (user?.id || ''),
      type: 'initial_assessment',
      appointmentNotes: ''
    });
    setShowModal(true);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await api.post('/appointments', {
        ...formData,
        appointmentDate: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime
      });
      toast.success('Appointment created');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await api.put(`/appointments/${selectedAppointment.id}/cancel`, {
          reason: 'Cancelled by staff'
        });
        toast.success('Appointment cancelled');
        setShowModal(false);
        fetchData();
      } catch (error) {
        toast.error('Failed to cancel appointment');
      }
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedAppointment) return;
    
    const appointmentId = selectedAppointment.id;
    setShowModal(false);
    setSelectedAppointment(null);
    
    try {
      await api.put(`/appointments/${appointmentId}/status`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleToggleHomeVisit = async (e, appointment) => {
    e.stopPropagation();
    try {
      await api.put(`/appointments/${appointment.id}/home-visit`);
      toast.success(appointment.isHomeVisit ? 'Home visit removed' : 'Marked as home visit');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appointment.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, day, time) => {
    e.preventDefault();
    if (!draggedAppointment) return;

    const newDate = format(day, 'yyyy-MM-dd');
    const [hours, minutes] = time.split(':').map(Number);
    const endHour = minutes === 30 ? hours + 1 : hours;
    const endMinutes = minutes === 30 ? '00' : '30';
    const newEndTime = `${endHour.toString().padStart(2, '0')}:${endMinutes}`;

    if (draggedAppointment.appointmentDate === newDate && 
        draggedAppointment.startTime.substring(0, 5) === time) {
      setDraggedAppointment(null);
      return;
    }

    try {
      await api.put(`/appointments/${draggedAppointment.id}/reschedule`, {
        appointmentDate: newDate,
        startTime: time,
        endTime: newEndTime
      });
      toast.success('Appointment moved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reschedule');
    }
    setDraggedAppointment(null);
  };

  const handleDragEnd = () => {
    setDraggedAppointment(null);
  };

  const handleTouchStart = (e, appointment) => {
    e.preventDefault();
    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
    setTouchDragAppointment(appointment);
  };

  const handleTouchMove = (e) => {
    if (!touchDragAppointment) return;
    e.preventDefault();
    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
    
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const slotElement = element?.closest('[data-slot]');
    if (slotElement) {
      setTouchTargetSlot(slotElement.dataset.slot);
    } else {
      setTouchTargetSlot(null);
    }
  };

  const handleEditClick = (e, appointment) => {
    e.stopPropagation();
    setEditingAppointment(appointment);
    setEditForm({
      appointmentDate: appointment.appointmentDate,
      startTime: appointment.startTime.substring(0, 5),
      endTime: appointment.endTime.substring(0, 5)
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/appointments/${editingAppointment.id}/reschedule`, {
        appointmentDate: editForm.appointmentDate,
        startTime: editForm.startTime,
        endTime: editForm.endTime
      });
      toast.success('Appointment updated');
      setShowEditModal(false);
      loadCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update');
    }
  };

  const handleTouchEnd = async (e) => {
    if (!touchDragAppointment) return;
    
    const touch = e.changedTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const slotElement = element?.closest('[data-slot]');
    
    if (slotElement) {
      const [dateStr, time] = slotElement.dataset.slot.split('|');
      const [hours, minutes] = time.split(':').map(Number);
      const endHour = minutes === 30 ? hours + 1 : hours;
      const endMinutes = minutes === 30 ? '00' : '30';
      const newEndTime = `${endHour.toString().padStart(2, '0')}:${endMinutes}`;

      if (touchDragAppointment.appointmentDate !== dateStr || 
          touchDragAppointment.startTime.substring(0, 5) !== time) {
        try {
          await api.put(`/appointments/${touchDragAppointment.id}/reschedule`, {
            appointmentDate: dateStr,
            startTime: time,
            endTime: newEndTime
          });
          toast.success('Appointment moved');
          loadCalendarData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to reschedule');
        }
      }
    }
    
    setTouchDragAppointment(null);
    setTouchTargetSlot(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#3498db',
      confirmed: '#2ecc71',
      checked_in: '#f39c12',
      in_progress: '#9b59b6',
      completed: '#27ae60',
      cancelled: '#e74c3c',
      no_show: '#95a5a6'
    };
    return colors[status] || '#3498db';
  };

  const getTypeLabel = (type) => {
    const labels = {
      initial_assessment: 'Initial Assessment',
      follow_up: 'Follow Up',
      treatment: 'Treatment',
      review: 'Review',
      discharge: 'Discharge'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading calendar...</p>
      </div>
    );
  }

  return (
    <div>
      {isMobile ? (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.25rem' }}>Calendar</h1>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--primary)', padding: '0.75rem 1rem', borderRadius: '8px', color: 'white' }}>
            <button onClick={() => setSelectedDay(addDays(selectedDay, -1))} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.5rem' }}>
              <ChevronLeft size={24} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600 }}>{format(selectedDay, 'EEEE')}</div>
              <div style={{ fontSize: '1.25rem' }}>{format(selectedDay, 'MMMM d, yyyy')}</div>
            </div>
            <button onClick={() => setSelectedDay(addDays(selectedDay, 1))} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.5rem' }}>
              <ChevronRight size={24} />
            </button>
          </div>
          <button 
            onClick={() => setSelectedDay(new Date())} 
            className="btn btn-secondary" 
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            Today
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ margin: 0 }}>Calendar</h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="form-control"
              style={{ width: 'auto', minWidth: '120px' }}
            >
              <option value="1day">1 Day</option>
              <option value="3day">3 Days</option>
              <option value="workweek">Work Week</option>
              <option value="6day">6 Days</option>
              <option value="7day">7 Days</option>
            </select>
            <button className="btn btn-secondary" onClick={() => {
              if (viewMode === '1day' || viewMode === '3day') {
                setSelectedDay(addDays(selectedDay, -1));
              } else {
                setCurrentWeekStart(subWeeks(currentWeekStart, 1));
              }
            }}>Previous</button>
            <button className="btn btn-secondary" onClick={() => {
              setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
              setSelectedDay(new Date());
            }}>Today</button>
            <button className="btn btn-secondary" onClick={() => {
              if (viewMode === '1day' || viewMode === '3day') {
                setSelectedDay(addDays(selectedDay, 1));
              } else {
                setCurrentWeekStart(addWeeks(currentWeekStart, 1));
              }
            }}>Next</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {!isMobile && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: `80px repeat(${weekDays.length}, 1fr)`,
            borderBottom: '1px solid var(--gray-200)'
          }}>
            <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRight: '1px solid var(--gray-200)', fontWeight: 600 }}>Time</div>
            {weekDays.map((day, index) => (
              <div key={index} style={{ padding: '1rem', textAlign: 'center', background: isSameDay(day, new Date()) ? 'var(--primary)' : 'var(--gray-50)', color: isSameDay(day, new Date()) ? 'white' : 'inherit', borderRight: index < weekDays.length - 1 ? '1px solid var(--gray-200)' : 'none', fontWeight: 600 }}>
                <div>{format(day, 'EEE')}</div>
                <div style={{ fontSize: '1.25rem' }}>{format(day, 'd')}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ maxHeight: isMobile ? 'none' : '600px', overflowY: 'auto' }}>
          {timeSlots.map((time, timeIndex) => {
            const daysToShow = isMobile ? [selectedDay] : weekDays;
            
            return (
              <div 
                key={time}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '60px 1fr' : `80px repeat(${weekDays.length}, 1fr)`,
                  borderBottom: timeIndex < timeSlots.length - 1 ? '1px solid var(--gray-100)' : 'none'
                }}
              >
                <div style={{ 
                  padding: '0.5rem', 
                  fontSize: '0.75rem',
                  color: 'var(--text-light)',
                  borderRight: '1px solid var(--gray-200)',
                  background: 'var(--gray-50)'
                }}>
                  {time}
                </div>
                {daysToShow.map((day, dayIndex) => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const dayAppointments = appointments.filter(apt => apt.appointmentDate === dayStr && apt.status !== 'cancelled');
                  const positionedAppointments = getOverlappingGroups(dayAppointments);
                  const startingPositioned = positionedAppointments.filter(p => p.appointment.startTime.substring(0, 5) === time);
                  const slotAppointments = getAppointmentsForSlot(day, time);
                  const isDragOver = draggedAppointment && slotAppointments.length === 0;
                  
                  return (
                    <div
                      key={dayIndex}
                      data-slot={`${format(day, 'yyyy-MM-dd')}|${time}`}
                      onClick={() => handleSlotClick(day, time)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, day, time)}
                      style={{
                        padding: '0',
                        minHeight: '40px',
                        borderRight: !isMobile && dayIndex < daysToShow.length - 1 ? '1px solid var(--gray-100)' : 'none',
                        cursor: 'pointer',
                        background: (isDragOver || touchTargetSlot === `${format(day, 'yyyy-MM-dd')}|${time}`) ? 'rgba(37, 99, 235, 0.3)' : (isSameDay(day, new Date()) ? 'rgba(37, 99, 235, 0.05)' : 'white'),
                        border: touchTargetSlot === `${format(day, 'yyyy-MM-dd')}|${time}` ? '2px dashed var(--primary)' : 'none',
                        position: 'relative',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => !draggedAppointment && (e.currentTarget.style.background = 'var(--gray-50)')}
                      onMouseLeave={(e) => !draggedAppointment && (e.currentTarget.style.background = isSameDay(day, new Date()) ? 'rgba(37, 99, 235, 0.05)' : 'white')}
                    >
                      {startingPositioned.map(({ appointment, column, totalColumns }) => {
                        const slotSpan = getSlotSpan(appointment.startTime.substring(0, 5), appointment.endTime.substring(0, 5));
                        const slotHeight = 40;
                        const appointmentHeight = (slotSpan * slotHeight) - 4;
                        const widthPercent = 100 / totalColumns;
                        const leftPercent = column * widthPercent;
                        // Only flash if same practitioner or same patient has overlapping appointments
                        const aptStart = timeToMinutes(appointment.startTime);
                        const aptEnd = timeToMinutes(appointment.endTime);
                        const hasRealConflict = positionedAppointments.some(p => {
                          if (p.appointment.id === appointment.id) return false;
                          const pStart = timeToMinutes(p.appointment.startTime);
                          const pEnd = timeToMinutes(p.appointment.endTime);
                          const timesOverlap = aptStart < pEnd && aptEnd > pStart;
                          if (!timesOverlap) return false;
                          // Only a conflict if same practitioner OR same patient
                          const samePractitioner = appointment.practitionerId === p.appointment.practitionerId;
                          const samePatient = appointment.patientId === p.appointment.patientId;
                          return samePractitioner || samePatient;
                        });
                        const isClashing = hasRealConflict;
                        return (
                        <div 
                          key={appointment.id}
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, appointment)}
                          onDragEnd={handleDragEnd}
                          onTouchStart={(e) => handleTouchStart(e, appointment)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          onClick={(e) => { e.stopPropagation(); setSelectedAppointment(appointment); setShowModal(true); }}
                          title={isClashing ? 'Booking Conflict' : ''}
                          style={{
                            background: getPractitionerColor(appointment.practitionerId),
                            color: 'white',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            fontSize: isMobile ? '0.75rem' : '0.65rem',
                            cursor: 'grab',
                            opacity: draggedAppointment?.id === appointment.id || touchDragAppointment?.id === appointment.id ? 0.5 : 1,
                            lineHeight: '1.2',
                            position: 'absolute',
                            top: '2px',
                            left: `calc(${leftPercent}% + 2px)`,
                            width: `calc(${widthPercent}% - 4px)`,
                            height: `${appointmentHeight}px`,
                            zIndex: 5,
                            touchAction: 'none',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            borderLeft: isClashing ? '3px solid #fbbf24' : 'none',
                            animation: isClashing ? 'clash-flash 1.5s ease-in-out infinite' : 'none'
                          }}>
                          <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appointment.patient?.firstName} {appointment.patient?.lastName}
                          </div>
                          <div style={{ fontSize: '0.6rem', opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appointment.practitioner?.firstName}
                          </div>
                          <div style={{ position: 'absolute', bottom: '2px', right: '2px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <Home 
                              size={10} 
                              style={{ 
                                opacity: appointment.isHomeVisit ? 1 : 0.4, 
                                cursor: 'pointer',
                                fill: appointment.isHomeVisit ? 'white' : 'none'
                              }} 
                              onClick={(e) => handleToggleHomeVisit(e, appointment)} 
                            />
                            <Pencil 
                              size={10} 
                              style={{ opacity: 0.7, cursor: 'pointer' }} 
                              onClick={(e) => handleEditClick(e, appointment)} 
                            />
                          </div>
                        </div>
                      );})}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {practitioners.map((practitioner, index) => (
          <div key={practitioner.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: practitionerColors[index % practitionerColors.length] }}></div>
            <span>{practitioner.firstName} {practitioner.lastName}</span>
          </div>
        ))}
      </div>

      {touchDragAppointment && (
        <div style={{
          position: 'fixed',
          left: touchPosition.x - 75,
          top: touchPosition.y - 30,
          width: '150px',
          background: getPractitionerColor(touchDragAppointment.practitionerId),
          color: 'white',
          padding: '0.5rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
          zIndex: 1000,
          opacity: 0.9
        }}>
          <div style={{ fontWeight: '500' }}>
            {touchDragAppointment.patient?.firstName} {touchDragAppointment.patient?.lastName}
          </div>
          <div style={{ fontSize: '0.65rem', opacity: 0.9 }}>
            Drag to new time
          </div>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowModal(false)}>
          <div 
            className="card" 
            style={{ 
              width: '100%', 
              maxWidth: '500px', 
              margin: '1rem',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            {selectedAppointment ? (
              <>
                <h2 style={{ marginTop: 0 }}>Appointment Details</h2>
                <div style={{ marginBottom: '1rem' }}>
                  <p><strong>Patient:</strong> {selectedAppointment.patient?.firstName} {selectedAppointment.patient?.lastName}</p>
                  <p><strong>Practitioner:</strong> {selectedAppointment.practitioner?.firstName} {selectedAppointment.practitioner?.lastName}</p>
                  <p><strong>Date:</strong> {format(parseISO(selectedAppointment.appointmentDate), 'EEEE, MMMM d, yyyy')}</p>
                  <p><strong>Time:</strong> {selectedAppointment.startTime} - {selectedAppointment.endTime}</p>
                  <p><strong>Type:</strong> {getTypeLabel(selectedAppointment.type)}</p>
                  <p><strong>Status:</strong> <span style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px', 
                    background: getStatusColor(selectedAppointment.status),
                    color: 'white',
                    fontSize: '0.875rem'
                  }}>{selectedAppointment.status.replace('_', ' ')}</span></p>
                  {selectedAppointment.isHomeVisit && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      background: '#fef3c7', 
                      borderRadius: '8px',
                      border: '1px solid #f59e0b'
                    }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Home size={16} /> Home Visit Address
                      </p>
                      {selectedAppointment.patient?.address ? (
                        <a 
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            `${selectedAppointment.patient.address}${selectedAppointment.patient.postcode ? ', ' + selectedAppointment.patient.postcode : ''}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            display: 'block',
                            margin: '0.5rem 0 0 0', 
                            color: '#1d4ed8', 
                            textDecoration: 'underline',
                            fontWeight: 500
                          }}
                        >
                          {selectedAppointment.patient.address}
                          {selectedAppointment.patient.postcode && `, ${selectedAppointment.patient.postcode}`}
                        </a>
                      ) : (
                        <p style={{ margin: '0.5rem 0 0 0', color: '#78350f' }}>No address on file</p>
                      )}
                      {selectedAppointment.patient?.phone && (
                        <a 
                          href={`tel:${selectedAppointment.patient.phone}`}
                          style={{ 
                            display: 'block',
                            margin: '0.5rem 0 0 0', 
                            color: '#1d4ed8', 
                            textDecoration: 'underline',
                            fontSize: '0.875rem'
                          }}
                        >
                          Tel: {selectedAppointment.patient.phone}
                        </a>
                      )}
                    </div>
                  )}
                  {selectedAppointment.appointmentNotes && (
                    <p><strong>Notes:</strong> {selectedAppointment.appointmentNotes}</p>
                  )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="form-label">Update Status</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {['confirmed', 'checked_in', 'in_progress', 'completed', 'no_show'].map(status => (
                      <button 
                        key={status}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.5rem' }}
                        onClick={() => handleUpdateStatus(status)}
                      >
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button 
                    className="btn"
                    style={{ background: '#10b981', color: 'white', border: 'none' }}
                    onClick={async () => {
                      const apt = selectedAppointment;
                      setShowModal(false);
                      try {
                        const response = await api.post('/invoices', {
                          patientId: apt.patientId,
                          practitionerId: apt.practitionerId,
                          appointmentId: apt.id
                        });
                        window.location.href = `/invoices/${response.invoice.id}`;
                      } catch (error) {
                        toast.error(error.message || 'Failed to create invoice');
                      }
                    }}
                  >
                    Create Invoice
                  </button>
                  <button 
                    className="btn btn-secondary"
                    style={{ background: '#e74c3c', color: 'white', border: 'none' }}
                    onClick={handleCancelAppointment}
                  >
                    Cancel Appointment
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ marginTop: 0 }}>New Appointment</h2>
                <p style={{ color: 'var(--text-light)', marginBottom: '1.5rem' }}>
                  {selectedSlot && format(parseISO(selectedSlot.date), 'EEEE, MMMM d, yyyy')} at {selectedSlot?.startTime}
                </p>
                
                <form onSubmit={handleCreateAppointment}>
                  <div className="form-group">
                    <label className="form-label">Patient *</label>
                    <select
                      className="form-input"
                      value={formData.patientId}
                      onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                      required
                    >
                      <option value="">Select a patient</option>
                      {patients.map(patient => (
                        <option key={patient.id} value={patient.id}>
                          {patient.firstName} {patient.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Practitioner *</label>
                    <select
                      className="form-input"
                      value={formData.practitionerId}
                      onChange={(e) => setFormData({ ...formData, practitionerId: e.target.value })}
                      required
                    >
                      <option value="">Select a practitioner</option>
                      {practitioners.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.firstName} {p.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Appointment Type</label>
                    <select
                      className="form-input"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="initial_assessment">Initial Assessment</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="treatment">Treatment</option>
                      <option value="review">Review</option>
                      <option value="discharge">Discharge</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-input"
                      rows="3"
                      value={formData.appointmentNotes}
                      onChange={(e) => setFormData({ ...formData, appointmentNotes: e.target.value })}
                      placeholder="Any notes for this appointment..."
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating...' : 'Create Appointment'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {showEditModal && editingAppointment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '1rem'
        }} onClick={() => setShowEditModal(false)}>
          <div 
            className="card" 
            style={{ width: '100%', maxWidth: '400px' }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Edit Appointment</h2>
            <form onSubmit={handleEditSave}>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={editForm.appointmentDate}
                  onChange={(e) => setEditForm({ ...editForm, appointmentDate: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary">Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
